// Components
import { useMemo } from 'react';
import { Box } from '@mui/material';
import { Theme } from '@mui/material';
import { SystemStyleObject } from '@mui/system';
import { useEllipsis } from '@Components/shared/styles/useEllipsis';
import { TableColumnHeader } from '@Components/shared/tables/columns';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

// Utils
import {
  RoleListColumns,
  RoleListFilter,
  roleListColumns,
} from '@utils/tables/users/roleListFilter';

// Contracts
import {
  TableColumnDescription,
  TableColumnDescriptionArray,
} from '@contracts/view/TableColumnDescription';
import { AviatorRoleItemDto } from '@contracts/api/dto/aviatorRoles/RoleDto';
import { Filterable } from '@contracts/common/traits/Filterable';
import { handleCopyClick } from '@Components/shared/copyText/CoptyText';
import { useSnackbar } from 'notistack';

type HeaderOptions = {
  filter: Filterable<RoleListFilter>;
};

type Columns = TableColumnDescriptionArray<
  AviatorRoleItemDto,
  HeaderOptions,
  typeof roleListColumns
>;

type Column = TableColumnDescription<AviatorRoleItemDto, HeaderOptions, RoleListColumns>;

const useSx = () => {
  return {
    id: { width: '80px' },
  } satisfies Record<string, SystemStyleObject<Theme>>;
};

export const useAviatorRoleTableColumns = () => {
  const ellipsis = useEllipsis();
  const styles = useSx();
  const { enqueueSnackbar } = useSnackbar();

  return useMemo(
    (): Column[] =>
      [
        {
          name: 'role.id',
          title: 'ID',
          sx: { minWidth: '150px', maxWidth: '130px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => (
            <Box
              sx={{
                ...ellipsis,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '4px',
                flexShrink: 1,
              }}
            >
              {row?.id}{' '}
              <ContentCopyIcon
                onClick={(event) => handleCopyClick(event, row?.id || '', enqueueSnackbar)}
              />
            </Box>
          ),
        },
        {
          name: 'role.name',
          title: 'Name',
          sx: { minWidth: '150px', maxWidth: '220px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.name}</Box>,
        },
        {
          name: 'role.description',
          title: 'Description',
          sx: { minWidth: '100px', maxWidth: '240px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.description}</Box>,
        },
        {
          name: 'role.isEditable',
          title: 'Is Editable',
          sx: { minWidth: '100px', maxWidth: '240px' },
          header: ({ title }) => <TableColumnHeader title={title} />,
          body: ({ row }) => <Box sx={{ ...ellipsis }}>{row?.isEditable ? 'True' : 'False'}</Box>,
        },
      ] satisfies Columns,
    [ellipsis]
  );
};
